import { useMemo } from "react";
import { SubscriptionPlan } from "enums/subscription-plans";
import { usePricingData } from "./usePricingData";
import useSubscription from "features/User/hooks/useSubscription";
import {
  isSubscriptionPaused,
  UserSubscription,
  getSubscriptionTier,
} from "helpers/subscription";

export type UpgradeTarget = {
  target: SubscriptionPlan;
  features: string[];
  comparisonPlan: SubscriptionPlan;
  churnkeyFeatures: string[];
};

/**
 * Custom hook for determining subscription upgrade paths and feature lists
 *
 * @returns {UpgradeTarget} Object containing:
 *   - target: The next subscription tier to upgrade to
 *   - features: Features list for the upgrade target
 *   - comparisonPlan: The plan to show for comparison (typically current tier or free)
 *   - churnkeyFeatures: Features for paused subscriptions (Churnkey flow)
 */
export const useSubscriptionUpgradeTarget = (): UpgradeTarget => {
  const { pricingData } = usePricingData();
  const { subscription } = useSubscription();
  const userSubscription = subscription?.user as UserSubscription;

  // Get subscription tier for smart upgrade path
  const subscriptionTier = useMemo(() => {
    return getSubscriptionTier(subscription);
  }, [subscription]);

  return useMemo(() => {
    // For paused subscriptions, show features for their actual tier (Churnkey flow)
    const getChurnkeyFeatures = (): string[] => {
      if (!userSubscription || !isSubscriptionPaused(userSubscription))
        return [];

      const tierMetadata = userSubscription.plan?.metadata?.type?.toLowerCase();
      switch (tierMetadata) {
        case "deep":
          return (
            pricingData?.deep?.features ?? pricingData?.premium?.features ?? []
          );
        case "premium":
          return pricingData?.premium?.features ?? [];
        case "team":
          return pricingData?.team?.features ?? [];
        default:
          return pricingData?.premium?.features ?? [];
      }
    };

    // For upgrade modals, determine next tier up
    const getUpgradeTarget = (): {
      target: SubscriptionPlan;
      features: string[];
    } => {
      switch (subscriptionTier) {
        case "free":
          return {
            target: SubscriptionPlan.PREMIUM,
            features: pricingData?.premium?.features ?? [],
          };
        case "premium":
          return {
            target: SubscriptionPlan.DEEP,
            features: pricingData?.deep?.features ?? [],
          };
        case "deep":
        case "team":
        case "enterprise":
        default:
          // For users who don't need upgrades, show premium as fallback
          return {
            target: SubscriptionPlan.PREMIUM,
            features: pricingData?.premium?.features ?? [],
          };
      }
    };

    const churnkeyFeatures = getChurnkeyFeatures();
    const upgrade = getUpgradeTarget();

    // Determine which plan to show for comparison
    const getComparisonPlan = (): SubscriptionPlan => {
      // For Deep upgrades, show Pro as comparison (Pro → Deep)
      if (upgrade.target === SubscriptionPlan.DEEP) {
        return SubscriptionPlan.PREMIUM;
      }
      // For all other upgrades, show Free as comparison (Free → Premium)
      return SubscriptionPlan.FREE;
    };

    return {
      target: upgrade.target,
      features: upgrade.features,
      comparisonPlan: getComparisonPlan(),
      churnkeyFeatures,
    };
  }, [subscriptionTier, pricingData, userSubscription]);
};
